// src/components/Optimization/ValueBridge.tsx
// PRC-046 (boceto G6): puente de valor en su bloque. Cada marca de la barra se
// identifica con su nombre: base, nuevo margen, cada referencia del modelo y
// potencial. Mismos datos y cálculo que antes.
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { formatPp, formatRatioPercent } from '../../modules/pricingJourney/formatNumber';

interface TacticalDataPoint {
  label: string;
  value: number;
}

interface ValueBridgeProps {
  base_historical_solid_cp: number;
  calculated_suggested_margin: number;
  mei_markup_mp_plus_mde: number;
  applied_strategy_profile: string;
  tacticalIntelligence: Record<string, string> | null;
}

const parseTacticalData = (tacticalIntel: Record<string, string> | null): TacticalDataPoint[] => {
  if (!tacticalIntel) return [];
  return Object.entries(tacticalIntel).map(([label, valueStr]) => {
    const numericValue = parseFloat(String(valueStr).replace('%', '').replace(',', '.')) / 100;
    return { label, value: numericValue };
  }).filter(item => !isNaN(item.value));
};

const clamp = (value: number) => Math.min(100, Math.max(0, value));

const ValueBridge: React.FC<ValueBridgeProps> = ({
  base_historical_solid_cp,
  calculated_suggested_margin,
  mei_markup_mp_plus_mde,
  tacticalIntelligence,
}) => {
  const totalOpportunity = mei_markup_mp_plus_mde - base_historical_solid_cp;
  const capturedOpportunity = calculated_suggested_margin - base_historical_solid_cp;

  const inRange = useMemo(
    () => parseTacticalData(tacticalIntelligence)
      .filter(item => item.value >= base_historical_solid_cp && item.value <= mei_markup_mp_plus_mde)
      .sort((a, b) => a.value - b.value),
    [tacticalIntelligence, base_historical_solid_cp, mei_markup_mp_plus_mde],
  );

  const positionOf = (value: number) => (totalOpportunity <= 0 ? 0 : clamp(((value - base_historical_solid_cp) / totalOpportunity) * 100));
  const newMarginPos = positionOf(calculated_suggested_margin);

  return (
    <Box component="section" sx={{ bgcolor: 'rgba(15,23,42,.72)', border: '1px solid #1e293b', borderRadius: '16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, p: '20px 24px', borderBottom: '1px solid #1e293b' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box component="h2" sx={{ fontSize: 16, fontWeight: 700, m: 0, color: '#f8fafc' }}>Puente de valor</Box>
          <Box component="p" sx={{ fontSize: 13, color: '#94a3b8', m: '4px 0 0' }}>
            Del margen que ya consigues al que el modelo ve posible, y cuánto captura tu estrategia.
          </Box>
        </Box>
        <Box component="span" sx={{ flexShrink: 0, maxWidth: 170, display: 'inline-flex', alignItems: 'center', minHeight: 26, py: '3px', lineHeight: 1.25, px: '10px', borderRadius: 999, fontSize: 12, fontWeight: 700, border: '1px solid #334155', bgcolor: 'rgba(148,163,184,.08)', color: '#cbd5e1' }}>
          Solo cuando hay histórico suficiente
        </Box>
      </Box>

      <Box sx={{ p: '20px 24px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Nombres de las referencias encima de su marca. */}
        {inRange.length > 0 && (
          <Box aria-hidden sx={{ position: 'relative', height: 18 * Math.min(inRange.length, 3) }}>
            {inRange.map((item, index) => (
              <Box
                key={item.label}
                component="span"
                sx={{
                  position: 'absolute', top: (index % 3) * 18, left: `${positionOf(item.value)}%`,
                  transform: positionOf(item.value) > 80 ? 'translateX(-100%)' : positionOf(item.value) < 20 ? 'none' : 'translateX(-50%)',
                  fontSize: 11, fontWeight: 700, color: '#cbd5e1', whiteSpace: 'nowrap',
                }}
              >
                {item.label} · {formatRatioPercent(item.value)}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ position: 'relative', height: 12, borderRadius: '6px', bgcolor: 'rgba(56,189,248,.2)' }}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${newMarginPos}%`, bgcolor: '#38bdf8', borderRadius: '6px 0 0 6px', transition: 'width .5s' }} />
          {inRange.map(item => (
            <Box
              key={item.label}
              role="img"
              aria-label={`${item.label}: ${formatRatioPercent(item.value)}`}
              sx={{ position: 'absolute', top: -5, bottom: -5, width: 2, bgcolor: '#fff', left: `${positionOf(item.value)}%` }}
            />
          ))}
        </Box>

        {/* Nombre de la marca del nuevo margen, bajo la barra en su posición. */}
        <Box aria-hidden sx={{ position: 'relative', height: 16, mt: '-8px' }}>
          <Box
            component="span"
            sx={{
              position: 'absolute', left: `${newMarginPos}%`,
              transform: newMarginPos > 85 ? 'translateX(-100%)' : newMarginPos < 15 ? 'none' : 'translateX(-50%)',
              fontSize: 11, fontWeight: 700, color: '#38bdf8', whiteSpace: 'nowrap',
            }}
          >
            Nuevo margen · {formatRatioPercent(calculated_suggested_margin)}
          </Box>
        </Box>

        {/* Como G6: base a la izquierda, nuevo margen al centro, potencial a la derecha. */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', color: '#64748b' }}>BASE</Box>
            <Box component="b" sx={{ fontSize: 18, color: '#94a3b8' }}>{formatRatioPercent(base_historical_solid_cp)}</Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'center' }}>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', color: '#38bdf8' }}>NUEVO MARGEN</Box>
            <Box component="b" sx={{ fontSize: 22, color: '#38bdf8' }}>{formatRatioPercent(calculated_suggested_margin)}</Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', color: '#64748b' }}>POTENCIAL</Box>
            <Box component="b" sx={{ fontSize: 18, color: '#94a3b8' }}>{formatRatioPercent(mei_markup_mp_plus_mde)}</Box>
          </Box>
        </Box>

        {inRange.length > 0 && (
          <Box>
            <Box component="span" sx={{ fontSize: 12, color: '#94a3b8' }}>Referencias del modelo dentro del rango:</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {inRange.map(item => (
                <Box key={item.label} component="span" sx={{ fontSize: 12, border: '1px solid #233149', borderRadius: '6px', px: 1, py: '3px', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
                  {item.label} · {formatRatioPercent(item.value)}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, borderTop: '1px solid #1e293b', pt: '14px' }}>
          <Box>
            <Box component="span" sx={{ fontSize: 13, color: '#94a3b8' }}>Oportunidad total</Box>
            <Box component="p" sx={{ fontSize: 18, fontWeight: 700, m: '4px 0 0', color: '#f8fafc' }}>{formatPp(totalOpportunity * 100)}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ fontSize: 13, color: '#94a3b8' }}>Impacto de la estrategia</Box>
            <Box component="p" sx={{ fontSize: 18, fontWeight: 700, m: '4px 0 0', color: '#38bdf8' }}>{formatPp(capturedOpportunity * 100)}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ValueBridge;
